// src/hooks/useAdminCheck.ts
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { checkAdminStatus } from '../services/admin/adminService';

interface AdminStatus {
  isAdmin: boolean;
  userId?: string;
  claims?: Record<string, any>;
}

export const useAdminCheck = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [adminStatus, setAdminStatus] = useState<AdminStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false); // 修復：初始狀態改為 false
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false); // 新增：使用 ref 追蹤 API 調用狀態

  useEffect(() => {
    const checkAdmin = async () => {

      // 如果沒有用戶或正在認證中，重置狀態
      if (!currentUser || authLoading) {
        // 只有在狀態需要更新時才更新
        setIsAdmin(false);
        setAdminStatus(null);
        setError(null);
        setLoading(false); // 沒有用戶時不需要 loading
        return;
      }

      // 使用 ref 防止重複調用（替換原本會造成死鎖的 if (loading) return）
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true; // 標記開始執行
      setLoading(true);
      setError(null);

      try {
        const status = await checkAdminStatus();

        setAdminStatus(status);
        setIsAdmin(status.isAdmin);

      } catch (err) {
        console.error('💥 useAdminCheck: API call failed', err);
        setError(err instanceof Error ? err.message : '檢查管理員狀態失敗');
        setIsAdmin(false);
        setAdminStatus(null);
      } finally {
        setLoading(false);
        isFetchingRef.current = false; // 標記執行結束
      }
    };

    checkAdmin();
  }, [currentUser, authLoading]); // 移除 isAdmin, loading, error 避免循環依賴

  // 監控 isAdmin 狀態變化
  useEffect(() => {
  }, [isAdmin, adminStatus, loading, error]);

  return {
    isAdmin,
    adminStatus,
    loading: loading || authLoading,
    error,
    refetch: async () => {
      if (currentUser && !authLoading && !isFetchingRef.current) {
        isFetchingRef.current = true; // 防止重複調用
        setLoading(true);
        try {
          const status = await checkAdminStatus();
          setAdminStatus(status);
          setIsAdmin(status.isAdmin);
          setError(null);
        } catch (err) {
          setError(err instanceof Error ? err.message : '檢查管理員狀態失敗');
        } finally {
          setLoading(false);
          isFetchingRef.current = false; // 確保重置標記
        }
      }
    }
  };
};